import { Controller } from "stimulus"
import PrescreenNotificationModule from "./shared_modules/prescreen_notification_module.js"

const CONSENT_RULE = {
  field: "prescreen_case[consent_file]",
  label: "Signed consent form",
  extensions: [".pdf", ".jpeg", ".jpg", ".png"],
  message: "Consent must be a PDF or image file (PDF, JPEG, or PNG)."
}

function uploadedFilename(zone) {
  const dropzone = zone.dropzone
  if (dropzone?.files?.length) {
    const file = dropzone.files[dropzone.files.length - 1]
    if (file?.name) return file.name
  }

  const nameEl = zone.querySelector("[data-dz-name]")
  if (nameEl?.textContent?.trim()) return nameEl.textContent.trim()

  return null
}

function fileExtension(filename) {
  if (!filename || !filename.includes(".")) return ""

  return filename.slice(filename.lastIndexOf(".")).toLowerCase()
}

export default class extends Controller {
  static targets = ["submit", "modalityCheckbox", "modalityPanel"]
  static values = { errors: Array, modalityRules: Object }

  connect() {
    this.notifications = new PrescreenNotificationModule()

    if (this.hasErrorsValue && this.errorsValue.length > 0) {
      this.notifications.error(this.errorsValue.join("\n"))
    }

    this.syncModalityPanels()
  }

  toggleModality() {
    this.syncModalityPanels()
  }

  syncModalityPanels() {
    if (!this.hasModalityPanelTarget) return

    const checkedKeys = new Set(this.checkedModalityKeys())

    const checkedIds = new Set(this.checkedModalityIds())

    this.modalityPanelTargets.forEach((panel) => {
      const id = panel.dataset.modalityId
      panel.classList.toggle("hidden", !checkedIds.has(id))
    })
  }

  checkedModalityIds() {
    if (!this.hasModalityCheckboxTarget) return []

    return this.modalityCheckboxTargets
      .filter((box) => box.checked)
      .map((box) => box.dataset.modalityId)
      .filter(Boolean)
  }

  checkedModalityKeys() {
    if (!this.hasModalityCheckboxTarget) return []

    return this.modalityCheckboxTargets.filter((box) => box.checked).map((box) => box.value)
  }

  validateDropzone(zone, rule, errors) {
    const input = zone.querySelector("[data-dropzone-target='input']")
    const fieldName = input?.name
    const label =
      zone.closest(".form-group")?.querySelector("label")?.textContent?.trim() ||
      rule?.label ||
      "A required file"
    const hasExisting = zone.dataset.prescreenExistingFile === "true"
    const hasUpload =
      zone.querySelector("input.attached_images") ||
      (fieldName && zone.querySelector(`input[type='hidden'][name='${fieldName}']`))

    if (!hasExisting && !hasUpload) {
      errors.push(`${label} is required.`)
      return
    }

    if (!hasUpload || !rule) return

    const filename = uploadedFilename(zone)
    if (!filename) return

    const extension = fileExtension(filename)
    const allowed = (rule.extensions || []).map((ext) => ext.toLowerCase())
    if (extension && allowed.length > 0 && !allowed.includes(extension)) {
      errors.push(rule.message || `${label} has an invalid file type.`)
    }
  }

  validate(event) {
    const errors = []

    const patientId = this.element.querySelector("#prescreen_case_patient_id")
    if (patientId && !patientId.value.trim()) {
      errors.push("Patient ID is required.")
    }

    const consentAttest = this.element.querySelector("#prescreen_case_consent_attested")
    if (consentAttest && !consentAttest.checked) {
      errors.push("Consent attestation is required.")
    }

    const checkedKeys = this.checkedModalityKeys()
    if (this.hasModalityCheckboxTarget && checkedKeys.length === 0) {
      errors.push("Select at least one imaging modality to submit.")
    }

    const rules = this.hasModalityRulesValue ? this.modalityRulesValue : {}

    this.checkedModalityIds().forEach((id) => {
      const rule = rules[id]
      if (!rule) return

      const panel = this.modalityPanelTargets.find((p) => p.dataset.modalityId === id)
      if (!panel) return

      const zone = panel.querySelector("[data-controller~='dropzone']")
      if (zone) this.validateDropzone(zone, rule, errors)
    })

    const consentZone = this.element.querySelector(
      `[data-controller~='dropzone'] input[name='${CONSENT_RULE.field}']`
    )?.closest("[data-controller~='dropzone']")

    if (consentZone) {
      this.validateDropzone(consentZone, CONSENT_RULE, errors)
    }

    if (errors.length > 0) {
      event.preventDefault()
      this.notifications.error(errors.join("\n"))
    }
  }
}
